import clsx from "clsx";
import { Drone, Eye, EyeOff, Lightbulb, MapPin, MountainSnow, RefreshCw, Route, ScanSearch, Shield, Star, Trees, Users, Waves, X } from "lucide-react";
import { type CSSProperties, type ReactNode, useEffect } from "react";

import type { Bilingual, Confidence, TerrainDrones, TerrainReport } from "../api/types";
import { Callout } from "../components/Callout";
import { EmptyState, Spinner } from "../components/EmptyState";
import { ChoiceCard, PanelRow, PropRow, StatTile } from "../components/panel";
import { Motion, Presence } from "../components/presence";
import { hexToRgb } from "../components/theme";
import { Badge, Button, FloatingPanel, IconButton, SectionLabel } from "../components/ui";
import { VIEW_META } from "../components/viewMeta";
import { HelpButton } from "../help/HelpButton";
import { k, lang, locale, t } from "../i18n/i18n";
import { FRIEND_BLUE } from "../map/units/symbols";
import { droneCoverText, smallDronesValue, TERRAIN_ANALYZE_TARGET } from "../map/drones/dronesFeatures";
import { LINK_LOSS_AMBER, OBSTACLE_GREEN } from "../map/terrain/TerrainLayers";
import { type MobilityLevel, type TerrainWeather, destroyedBridge, weatherConfidence, withWeather } from "../map/terrain/terrainWeather";
import { useTerrainAnalysis } from "../map/terrain/useTerrainAnalysis";
import { designateZoneWait } from "../map/zones/zoneAttention";
import { usePendingZone, ZoneWaitCallout } from "../map/zones/ZoneWaitCallout";
import { useUi } from "../state/ui";
import { OpenMissionDataButton, useOpenMissionData } from "./openMissionData";
import { chokepointColor, landcoverColors } from "./terrainColors";
import { Waiting } from "../components/Waiting";
import { assessmentTone, ceilingText, climateLabel, num as weatherNum, temperatureText, visibilityText, windText } from "../weather";
import { pictogramIcon } from "../weatherIcons";

/** Analyse du terrain (vue « Analyse du terrain », registre H) : analyse de la zone d'opérations ou d'une zone tracée sur la
 *  carte, selon la méthode OAKOC, comme le rapport d'analyse de zone de l'iPad — cartouche daté, verdict orienté
 *  par la mission, tuiles, bilan, conclusions, terrain clé, sections OAKOC avec leur confiance, avertissements —,
 *  et axe favorable à travers la zone.
 *
 *  Météo du dossier (chantier de cohérence météo) : rubrique Météo (conditions, effets par facteur et leurs
 *  conséquences pour l'ami et pour l'ENI), pastille de l'appréciation, valeurs « par beau temps » à côté de celles
 *  du jour quand elles diffèrent ; rapport périmé signalé quand la météo, la date ou le fuseau du dossier ont
 *  changé depuis le calcul, avec « Relancer l'analyse ». Rien de nouveau en CENTAC (pas de météo). Qualificatif du
 *  coefficient de mobilité sur l'échelle unique des ordres, rendu par le serveur. */

const ROAD_LABEL: Record<string, string> = {
  motorway: k("Autoroute"),
  trunk: k("Voie rapide"),
  primary: k("Route primaire"),
  secondary: k("Route secondaire"),
  tertiary: k("Route tertiaire"),
};

/** Classe de route OpenStreetMap dans la langue de l'interface (la classe brute si elle est inconnue). */
function roadLabel(roadClass: string): string {
  const label = ROAD_LABEL[roadClass];
  return label ? t(label) : roadClass;
}

/** Confiance d'une section, en pastille d'état (R6). */
const CONFIDENCE: Record<Confidence, { label: string; tone: "ok" | "warn" | "danger" }> = {
  high: { label: k("Élevée"), tone: "ok" },
  medium: { label: k("Moyenne"), tone: "warn" },
  low: { label: k("Faible"), tone: "danger" },
};

/** Texte du rapport dans la langue de l'interface : le moteur le rédige en français et en anglais, comme l'analyse
 *  de zone de l'iPad (`report.verdict.text(isEnglish)`). */
const text = (value: Bilingual) => (lang() === "en" && value.en ? value.en : value.fr);

/** Pourcentage : « 35 % » en français, « 35% » en anglais. */
const percent = (value: number | string) => t("{n} %", { n: value });

/** GDH de la production (« 291015Z SEP 26 »), comme le cartouche d'état-major de l'iPad : heure du calcul. */
function dtg(date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  return `${pad(date.getUTCDate())}${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}Z ${months[date.getUTCMonth()]} ${String(date.getUTCFullYear()).slice(2)}`;
}

const num = (value: number, digits = 1) => value.toLocaleString(locale(), { maximumFractionDigits: digits });

/** Section du rapport (méthode OAKOC) : lettre et icône grises, titre, confiance en pastille ; filet en haut et
 *  gouttière de 16 px, comme une section de fiche. */
function ReportSection({
  letter,
  icon,
  title,
  confidence,
  children,
}: {
  letter?: string;
  icon: ReactNode;
  title: string;
  confidence?: Confidence | null;
  children: ReactNode;
}) {
  return (
    <section className="border-t border-line px-4 py-3">
      <h3 className="mb-2 flex items-center gap-2">
        {letter && (
          // Lettre OAKOC : Observation, Axes d'approche, terrain clé (Key terrain), Obstacles, Couvert.
          <span className="inline-flex size-5 items-center justify-center rounded-md border border-line-strong font-mono text-meta font-semibold text-ink-muted">
            {letter}
          </span>
        )}
        <span className="flex text-ink-muted">{icon}</span>
        <span className="flex-1 text-ui font-semibold text-ink">{title}</span>
        {confidence && (
          <Badge dot tone={CONFIDENCE[confidence].tone} title={t("Confiance, calculée d'après la donnée la plus douteuse de la section")}>
            {t(CONFIDENCE[confidence].label)}
          </Badge>
        )}
      </h3>
      <div className="space-y-1.5">{children}</div>
    </section>
  );
}

/** Ligne du rapport : propriété (libellé gris, valeur à droite). */
function ReportRow({ label, children }: { label: ReactNode; children: ReactNode }) {
  return <PropRow label={label}>{children}</PropRow>;
}

/** Objet du rapport qu'on peut localiser (pont, point haut, point de passage obligé) : ligne cliquable qui centre
 *  la carte dessus. */
function LocateRow({
  icon,
  title,
  detail,
  state,
  onClick,
}: {
  icon?: ReactNode;
  title: ReactNode;
  detail?: ReactNode;
  state?: ReactNode;
  onClick: () => void;
}) {
  return <PanelRow icon={icon} title={title} detail={detail} state={state} onClick={onClick} clickTitle={t("Centrer la carte")} className="-mx-2" />;
}

function Bullet({ children }: { children: ReactNode }) {
  return (
    <p className="flex gap-2 text-ui leading-relaxed text-ink">
      <span className="text-ink-muted">•</span>
      <span>{children}</span>
    </p>
  );
}

/** Vert des obstacles (APP-6), celui des hachures de la carte, en composantes « r g b ». */
const GREEN_RGB = hexToRgb(OBSTACLE_GREEN) ?? "0 138 46";

/** Motifs MCOO de la légende et de la barre de praticabilité : hachures simples (SLOW) ou croisées (NO-GO). */
const hatch = (cross: boolean) => ({
  backgroundColor: `rgb(${GREEN_RGB} / 0.14)`,
  backgroundImage: cross
    ? `repeating-linear-gradient(45deg, rgb(${GREEN_RGB} / 0.9) 0 1.5px, transparent 1.5px 6px), repeating-linear-gradient(-45deg, rgb(${GREEN_RGB} / 0.9) 0 1.5px, transparent 1.5px 6px)`
    : `repeating-linear-gradient(-45deg, rgb(${GREEN_RGB} / 0.9) 0 1.5px, transparent 1.5px 6px)`,
});

function PercentBar({ segments }: { segments: { label: string; pct: number; style: CSSProperties }[] }) {
  const shown = segments.filter((s) => s.pct > 0);
  return (
    <div>
      <div className="flex h-3.5 overflow-hidden rounded-md ring-1 ring-line">
        {shown.map((s, i) => (
          <div key={s.label} style={{ ...s.style, flex: i === shown.length - 1 ? "1 1 0" : `0 0 ${Math.max(s.pct, 2)}%` }} />
        ))}
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
        {segments.map((s) => (
          <span key={s.label} className={clsx("flex items-center gap-1.5 text-meta text-ink-soft", s.pct === 0 && "opacity-40")}>
            <span className="size-2.5 rounded-sm ring-1 ring-line-strong" style={s.style} />
            {s.label} {percent(s.pct)}
          </span>
        ))}
      </div>
    </div>
  );
}

const SEVERITY_LABEL: Record<string, string> = { HIGH: k("forte"), MEDIUM: k("moyenne") };

/** Couleur du qualificatif de mobilité (ton de la puce de la mesure au survol, `mobility.LEVELS` du serveur). */
const LEVEL_CLASS: Record<MobilityLevel["tone"], { text: string; bar: string }> = {
  ok: { text: "text-ok", bar: "bg-ok" },
  warn: { text: "text-warn", bar: "bg-warn" },
  brand: { text: "text-danger", bar: "bg-danger" },
};

/** Mois de la météo de l'analyse, dans la langue lue (« novembre »). */
const monthName = (month: number) => new Date(2000, month - 1, 1).toLocaleDateString(locale(), { month: "long" });

/** Conditions de la rubrique Météo : « Saisonnière (novembre, climat océanique) — défavorable », ou le libellé de la
 *  météo choisie ou du scénario. */
function conditionsText(weather: TerrainWeather): string {
  const origin =
    weather.source === "seasonal"
      ? t("Saisonnière ({month}, {climate})", { month: monthName(weather.month), climate: climateLabel(weather.climate) })
      : text(weather.label);
  return `${origin} — ${text(weather.assessment)}`;
}

/** Effet de la météo : texte en puce, puis la conséquence pour l'ami et pour l'ENI quand elle existe. */
function WeatherEffect({ effect }: { effect: TerrainWeather["effects"][number] }) {
  return (
    <div className="space-y-0.5">
      <Bullet>{text(effect.text)}</Bullet>
      {effect.friendly && <p className="pl-4 text-meta leading-snug text-ink-soft">{t("Ami : {text}", { text: text(effect.friendly) })}</p>}
      {effect.enemy && <p className="pl-4 text-meta leading-snug text-ink-soft">{t("ENI : {text}", { text: text(effect.enemy) })}</p>}
    </div>
  );
}

const SITE_COVER: Record<TerrainDrones["launch_sites"][number]["cover"], string> = {
  forest: k("sous bois"),
  urban: k("dans le bâti"),
  edge: k("en lisière"),
};

/** Section « Drones » du rapport (chantier « Drones ») : zones de mise en œuvre (cliquables : la carte s'y centre),
 *  couverts contre les drones ennemis, risque de perte de liaison par le relief, et « Montrer sur la carte » qui
 *  coche le calque « Éléments drones de l'analyse du terrain ». */
function DronesReport({ drones, phase, fly }: { drones: TerrainDrones; phase: string; fly: (lon: number, lat: number) => void }) {
  const setControlVisible = useUi((s) => s.setControlVisible);
  const sites = drones.launch_sites;
  const km = (m: number) => num(m / 1000);
  const show = () => {
    setControlVisible("droneTerrain", true);
    const best = sites[0];
    if (best) fly(best.lon, best.lat);
  };
  return (
    <ReportSection icon={<Drone size={14} />} title={t("Drones")}>
      {!drones.available ? (
        <p className="text-ui leading-relaxed text-ink-soft">
          {drones.reason === "no_enemy"
            ? t("Placez l'ennemi pour situer les zones de mise en œuvre.")
            : t("Relief indisponible : zones de mise en œuvre et perte de liaison non évaluées.")}
        </p>
      ) : (
        <>
          <ReportRow label={t("Zones de mise en œuvre")}>{sites.length || t("aucune")}</ReportRow>
          {sites.map((site, i) => (
            <LocateRow
              key={`${site.lon},${site.lat}`}
              icon={<span className="w-4 shrink-0 text-center font-mono text-meta font-semibold" style={{ color: FRIEND_BLUE }}>{i + 1}</span>}
              title={site.name || t("Zone {n}", { n: i + 1 })}
              detail={t("{cover} · à {km} km de l'ennemi", { cover: t(SITE_COVER[site.cover]), km: km(site.distance_eni_m) })}
              state={<span className="text-meta text-ink-muted tabular-nums">{t("portée {pct}", { pct: percent(site.reach_pct) })}</span>}
              onClick={() => fly(site.lon, site.lat)}
            />
          ))}
          {sites.length === 0 && (
            <p className="text-meta leading-snug text-ink-muted">
              {drones.reason === "far"
                ? t("Au temps {phase}, nos unités sont trop loin de l'ennemi : une zone de mise en œuvre se situe entre 1,5 et 5 km de lui, à 1,5 km au plus d'une unité amie.", { phase })
                : t("Aucun point à la fois défilé aux vues ennemies, sous couvert et hors de sa menace : terrain trop ouvert face à l'ennemi.")}
            </p>
          )}
          {sites.length > 0 && (
            <p className="text-meta leading-snug text-ink-muted">
              {t("Défilées aux vues de l'ennemi au sol, sous couvert, hors de sa menace ; portée : terrain côté ennemi vu d'un drone à 120 m sol dans un rayon de {km} km.", { km: km(drones.range_m) })}
            </p>
          )}
        </>
      )}
      <ReportRow label={t("Couverts contre les drones ennemis")}>{percent(drones.cover_pct)}</ReportRow>
      <p className="text-meta leading-snug text-ink-soft">{droneCoverText(drones.cover_pct)}</p>
      {drones.available && drones.link_loss_pct !== null && (
        <>
          <ReportRow label={t("Risque de perte de liaison par le relief")}>{percent(drones.link_loss_pct)}</ReportRow>
          <p className="flex items-center gap-1.5 text-meta leading-snug text-ink-muted">
            <span aria-hidden className="size-2.5 shrink-0 rounded-sm" style={{ background: LINK_LOSS_AMBER, opacity: 0.6 }} />
            {t("Part du rayon de {km} km masquée par le relief depuis la zone 1.", { km: km(drones.range_m) })}
          </p>
        </>
      )}
      {drones.available && (sites.length > 0 || drones.masked.length > 0) && (
        <div className="pt-1">
          <Button size="sm" variant="secondary" onClick={show}>
            <MapPin size={14} /> {t("Montrer sur la carte")}
          </Button>
        </div>
      )}
    </ReportSection>
  );
}

/** Rubrique Météo du rapport : conditions de l'exercice et effets sur le terrain par facteur. */
function WeatherReport({ weather, confidence }: { weather: TerrainWeather; confidence: Confidence | null }) {
  const Icon = pictogramIcon(weather.pictogram);
  return (
    <ReportSection icon={<Icon size={14} />} title={t("Météo")} confidence={confidence}>
      <ReportRow label={t("Conditions")}>{conditionsText(weather)}</ReportRow>
      <ReportRow label={t("Visibilité")}>{visibilityText(weather.visibility_km)}</ReportRow>
      {/* Mêmes intitulés et mêmes grandeurs que l'aperçu du réglage Météo. */}
      <ReportRow label={t("Plafond")}>{ceilingText(weather.ceiling_ft)}</ReportRow>
      <ReportRow label={t("Vent")}>{windText(weather.wind_kmh)}</ReportRow>
      <ReportRow label={t("Température")}>{temperatureText(weather.temp)}</ReportRow>
      {/* État des petits drones (chantier « Drones ») : à côté de l'état de vol des aéronefs, jamais contradictoire. */}
      {weather.small_drones && <ReportRow label={t("Petits drones")}>{smallDronesValue(weather.small_drones)}</ReportRow>}
      {weather.active && <ReportRow label={t("État du sol")}>{text(weather.ground_label)}</ReportRow>}
      {weather.active && weather.delay_pct > 0 && (
        <ReportRow label={t("Délais de mouvement")}>{t("+{p} %", { p: weather.delay_pct })}</ReportRow>
      )}
      {weather.effects.length > 0 ? (
        <div className="space-y-2 pt-1">
          {weather.effects.map((effect) => (
            <WeatherEffect key={effect.factor} effect={effect} />
          ))}
        </div>
      ) : (
        <p className="pt-1 text-ui leading-relaxed text-ink-soft">{t("Aucun effet notable sur le terrain par ce temps.")}</p>
      )}
    </ReportSection>
  );
}

/** Rapport périmé : la météo, la date de début ou le fuseau du dossier ont changé depuis le calcul. « Relancer
 *  l'analyse » refait la même zone (l'axe aussi s'il était affiché) ; bloqué comme les autres analyses, il désigne
 *  l'action attendue. */
export function StaleCallout({
  show,
  busy,
  onRerun,
  blocked,
  onBlocked,
}: {
  show: boolean;
  busy: boolean;
  onRerun: () => void;
  blocked: boolean;
  onBlocked: () => void;
}) {
  return (
    <Presence show={show}>
      <Motion origin="top" className="px-4 pt-3">
        <Callout
          tone="info"
          action={
            <Button size="sm" variant="secondary" onClick={onRerun} disabled={busy} blocked={blocked} onBlocked={onBlocked}>
              <RefreshCw size={14} /> {t("Relancer l'analyse")}
            </Button>
          }
        >
          {t("La météo ou la date de début a changé depuis cette analyse.")}
        </Callout>
      </Motion>
    </Presence>
  );
}

/** Relief de l'analyse (`dem_source`) : relief fin de l'IGN, de Terrarium ou de Copernicus GLO-30 au pas
 *  donné ; sinon la grille du camp, celle de la zone ou les altitudes OpenStreetMap (zone libre sans relief fin). */
export function Report({ report, onAxis, axisBusy }: { report: TerrainReport; onAxis: () => void; axisBusy: boolean }) {
  const requestFlyTo = useUi((s) => s.requestFlyTo);
  const fly = (lon: number, lat: number) => requestFlyTo([lon, lat], 13);
  const extra = withWeather(report);
  const weather = extra.weather ?? null;
  const fair = weather?.active ? weather.fair : null;
  const mobility = report.mobility;
  const high = report.obstacles.chokepoints.filter((c) => c.severity === "HIGH").length;
  const verdictTone = report.verdict_tone === "favorable" ? "ok" : report.verdict_tone === "warning" ? "warn" : "neutral";
  const coeff = report.mobility_coeff;
  // Qualificatif sur l'échelle unique des ordres et de la mesure au survol, rendu par le serveur : le panneau n'a
  // plus de seuils à lui.
  const level = extra.mobility_level ?? null;
  const levelClass = level ? LEVEL_CLASS[level.tone] : null;
  const cover = landcoverColors();
  const fairMobility = fair?.mobility ?? null;
  const mobilityChanged =
    Boolean(fairMobility && mobility) &&
    (fairMobility!.go !== mobility!.go || fairMobility!.slow !== mobility!.slow || fairMobility!.no_go !== mobility!.no_go);
  const fairCoeff = fair && Math.abs(fair.mobility_coeff - coeff) >= 0.005 ? fair.mobility_coeff : null;
  const fairThreat = fair && fair.threat_pct !== report.dispositions.threat_pct ? fair.threat_pct : null;
  const fairBadge = fair && text(fair.verdict_badge) !== text(report.verdict_badge) ? fair.verdict_badge : null;
  // Vue du jour bornée par la visibilité, ou nulle quand le point haut principal est dans les nuages.
  const limitedView = Boolean(weather?.active && (weather.high_point_in_cloud || weather.observation_limit_km !== null));

  return (
    <Motion origin="fade">
      <div className="space-y-3 px-4 pt-3 pb-3">
        {/* Cartouche d'état-major : la production est datée (heure du calcul) et localisée. */}
        <div className="flex items-center gap-2 rounded-lg border border-line px-2.5 py-1.5">
          <SectionLabel inset>{t("Analyse terrain")}</SectionLabel>
          <span className="min-w-0 flex-1 truncate font-mono text-meta font-semibold text-ink" title={report.map_name}>
            {dtg(extra.computed_at ? new Date(extra.computed_at) : undefined)} · {report.map_name.toUpperCase()}
          </span>
        </div>
        <div className="text-meta text-ink-muted">
          {t("Méthode OAKOC")} ·{" "}
          {report.camp ? t("analyse de la zone d'opérations (emprise de l'exercice)") : t("zone tracée sur la carte")} ·{" "}
          {t("dispositif au temps {phase}", { phase: report.phase ?? "T1" })}
          {weather &&
            ` · ${weather.source === "seasonal" ? t("météo saisonnière") : t("météo : {label}", { label: text(weather.label) })}`}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge dot tone={verdictTone}>
            {text(report.verdict_badge)}
          </Badge>
          {weather?.active && (
            <Badge dot tone={assessmentTone(weather.severity)}>
              {t("Météo : {assessment}", { assessment: text(weather.assessment) })}
            </Badge>
          )}
        </div>
        {fairBadge && <p className="text-meta text-ink-muted">{t("Par beau temps : {badge}", { badge: text(fairBadge) })}</p>}
        <div className="grid grid-cols-2 gap-1.5">
          <StatTile value={num(report.area_km2, report.area_km2 < 10 ? 1 : 0)} unit="km²" label={t("Surface")} />
          <StatTile
            value={mobility ? String(mobility.go) : "—"}
            unit="% GO"
            label={t("Praticabilité")}
            tone={mobility ? (mobility.go >= 60 ? "ok" : mobility.go >= 30 ? "warn" : "danger") : undefined}
            hint={
              fairMobility && mobility && fairMobility.go !== mobility.go
                ? t("par beau temps : {v} % GO", { v: fairMobility.go })
                : undefined
            }
          />
          <StatTile
            value={String(report.dispositions.threat_pct)}
            unit="%"
            label={t("Menace ENI")}
            tone={report.dispositions.threat_pct >= 40 ? "danger" : report.dispositions.threat_pct > 0 ? "warn" : undefined}
            hint={fairThreat !== null ? t("par beau temps : {v} %", { v: fairThreat }) : undefined}
          />
          <StatTile value={String(high)} label={t("Points de passage obligés")} />
        </div>
        {/* Le bilan : la phrase que le chef lit en premier. */}
        <p className="rounded-lg bg-canvas px-3 py-2 text-title leading-relaxed text-ink">{text(report.verdict)}</p>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            onClick={onAxis}
            disabled={axisBusy || !mobility}
            title={mobility ? t("Itinéraire tout-terrain le plus favorable à travers la zone, pondéré par la menace ennemie") : t("Praticabilité non évaluée : pas d'axe calculable")}
          >
            {axisBusy ? <Spinner decorative /> : <Route size={14} />}{" "}
            {axisBusy ? <Waiting>{t("Calcul de l'axe…")}</Waiting> : report.axis ? t("Recalculer l'axe") : t("Axe favorable")}
          </Button>
        </div>
        <Presence show={report.axis !== undefined}>
          <Motion origin="top" className="text-ui leading-snug text-ink-soft">
            {report.axis
              ? t(
                  "Axe favorable tout-terrain à travers la zone : {km} km, {pct} % sous menace ENI (souligné de rouge, portées ennemies en tireté rouge).",
                  { km: num(report.axis.total_m / 1000), pct: report.axis.exposed_pct },
                )
              : t("Aucun axe calculable ici (pas de grille de praticabilité).")}
          </Motion>
        </Presence>
      </div>

      {report.conclusions.length > 0 && (
        <ReportSection icon={<Lightbulb size={14} />} title={t("Conclusions tactiques")}>
          {report.conclusions.map((c) => (
            <Bullet key={c.fr}>{text(c)}</Bullet>
          ))}
        </ReportSection>
      )}

      {weather && <WeatherReport weather={weather} confidence={weatherConfidence(report)} />}

      {report.key_terrain.length > 0 && (
        <ReportSection letter="K" icon={<Star size={14} />} title={t("Terrain clé")} confidence={report.confidence.obstacles}>
          {report.key_terrain.map((item, i) => (
            <p key={item.fr} className="flex gap-2 text-ui leading-relaxed text-ink">
              <span className="font-semibold text-ink-muted tabular-nums">{i + 1}.</span>
              <span>{text(item)}</span>
            </p>
          ))}
        </ReportSection>
      )}

      <ReportSection letter="A" icon={<Route size={14} />} title={t("Axes d'approche et mobilité")} confidence={report.confidence.mobility}>
        {mobility ? (
          <>
            {report.coverage_pct < 100 && (
              <Callout tone="warn">{t("Évaluée sur la partie couverte de la zone ({pct} %).", { pct: report.coverage_pct })}</Callout>
            )}
            <PercentBar
              segments={[
                { label: "GO", pct: mobility.go, style: { backgroundColor: "var(--color-surface)" } },
                { label: "SLOW", pct: mobility.slow, style: hatch(false) },
                { label: "NO-GO", pct: mobility.no_go, style: hatch(true) },
              ]}
            />
            {mobilityChanged && fairMobility && (
              <p className="text-meta text-ink-muted">
                {t("Par beau temps : {go} % GO, {slow} % SLOW, {nogo} % NO-GO", {
                  go: fairMobility.go,
                  slow: fairMobility.slow,
                  nogo: fairMobility.no_go,
                })}
              </p>
            )}
          </>
        ) : (
          <ReportRow label={t("Praticabilité")}>{t("non évaluée (hors de l'emprise des données)")}</ReportRow>
        )}
        <div>
          <ReportRow label={report.camp ? t("Coefficient de mobilité de l'emprise") : t("Coefficient de mobilité de la zone")}>
            <span className={levelClass?.text}>
              {num(coeff, 2)}
              {level && ` — ${text(level.label)}`}
            </span>
          </ReportRow>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-canvas">
            <div
              className={clsx("h-full rounded-full", levelClass?.bar ?? "bg-ink-muted")}
              style={{ width: `${Math.max(4, ((coeff - 0.3) / 0.7) * 100)}%` }}
            />
          </div>
          {fairCoeff !== null && <p className="mt-1 text-meta text-ink-muted">{t("Par beau temps : {v}", { v: num(fairCoeff, 2) })}</p>}
        </div>
        {report.major_axes > 0 && (
          <ReportRow label={t("Axes majeurs traversants")}>
            {report.major_axes}
            {report.axis_names.length > 0 && ` — ${report.axis_names.join(", ")}`}
          </ReportRow>
        )}
        {report.roads.map((r) => (
          <ReportRow key={r.class} label={roadLabel(r.class)}>
            {num(r.km)} km
          </ReportRow>
        ))}
        {report.roads.length === 0 && <ReportRow label={t("Réseau routier")}>{t("absent de la zone")}</ReportRow>}
      </ReportSection>

      <ReportSection letter="O" icon={<Waves size={14} />} title={t("Obstacles et franchissements")} confidence={report.confidence.obstacles}>
        <ReportRow label={t("Cours d'eau traversants")}>
          {report.obstacles.river_crossings}
          {report.obstacles.river_km > 0.05 && ` ${t("({km} km dans la zone)", { km: num(report.obstacles.river_km) })}`}
        </ReportRow>
        {weather?.active && weather.flood && <ReportRow label={t("Par ce temps")}>{t("en crue, gués noyés")}</ReportRow>}
        {report.obstacles.bridges.length === 0 && <ReportRow label={t("Ponts")}>{t("aucun recensé")}</ReportRow>}
        {report.obstacles.bridges.map((b) => (
          <LocateRow
            key={`${b.lon},${b.lat}`}
            title={b.name}
            detail={
              <>
                {b.max_load_t !== null ? `${num(b.max_load_t, 0)} t` : "—"}
                {b.width_m !== null && ` · ${num(b.width_m, 0)} m`}
              </>
            }
            state={
              destroyedBridge(b) ? (
                <Badge dot tone="danger">
                  {t("détruit")}
                </Badge>
              ) : (
                b.unverified && (
                  <Badge dot tone="warn">
                    {t("non vérifié")}
                  </Badge>
                )
              )
            }
            onClick={() => fly(b.lon, b.lat)}
          />
        ))}
        {report.obstacles.chokepoints.length > 0 && (
          <>
            <ReportRow label={t("Points de passage obligés")}>{report.obstacles.chokepoints.length}</ReportRow>
            {report.obstacles.chokepoints.slice(0, 6).map((c) => (
              <LocateRow
                key={`${c.lon},${c.lat}`}
                icon={<span className="size-2 shrink-0 rounded-full" style={{ background: chokepointColor(c.severity) }} />}
                title={text(c.detail)}
                state={<span className="text-meta text-ink-muted">{t(SEVERITY_LABEL[c.severity] ?? k("faible"))}</span>}
                onClick={() => fly(c.lon, c.lat)}
              />
            ))}
            {report.obstacles.chokepoints.length > 6 && (
              <p className="text-meta text-ink-muted">
                {t("… et {n} autres (points verts sur la carte).", { n: report.obstacles.chokepoints.length - 6 })}
              </p>
            )}
          </>
        )}
      </ReportSection>

      {report.relief && (
        <ReportSection letter="O" icon={<MountainSnow size={14} />} title={t("Observation et champs de tir")} confidence={report.confidence.relief}>
          <ReportRow label={t("Altitudes")}>
            {report.relief.elev_min} – {report.relief.elev_max} m (Δ {report.relief.elev_max - report.relief.elev_min} m)
          </ReportRow>
          <ReportRow label={t("Pente (moyenne / max.)")}>
            {percent(num(report.relief.slope_mean, 0))} / {percent(num(report.relief.slope_max, 0))}
          </ReportRow>
          {report.relief.high_points.map((hp) => (
            <LocateRow
              key={`${hp.lon},${hp.lat}`}
              title={hp.name ?? t("sans nom")}
              detail={`${t("Point haut")} — ${hp.elevation_m} m`}
              onClick={() => fly(hp.lon, hp.lat)}
            />
          ))}
          {report.relief.observation_pct !== null && (
            <>
              <ReportRow label={t("Vu du point haut principal")}>~{percent(report.relief.observation_pct)}</ReportRow>
              {limitedView && weather && (
                <ReportRow label={t("Vu par ce temps")}>
                  {weather.high_point_in_cloud
                    ? t("aucune — point haut dans les nuages")
                    : t("~{pct} — vue limitée à {km} km", {
                        pct: percent(weather.observation_pct ?? 0),
                        km: weatherNum(weather.observation_limit_km ?? 0),
                      })}
                </ReportRow>
              )}
              <p className="flex items-center gap-1.5 text-meta text-ink-muted">
                <span className="size-2 rounded-full" style={{ background: FRIEND_BLUE }} />{" "}
                {limitedView
                  ? t("Points bleus sur la carte : terrain vu par ce temps depuis ce point haut.")
                  : t("Points bleus sur la carte : terrain vu depuis ce point haut.")}
              </p>
            </>
          )}
        </ReportSection>
      )}

      <ReportSection letter="C" icon={<Trees size={14} />} title={t("Couvert et dissimulation")} confidence={report.confidence.cover}>
        {report.cover.available ? (
          <PercentBar
            segments={[
              { label: t("Forêt"), pct: report.cover.forest, style: { backgroundColor: cover.forest } },
              { label: t("Bâti"), pct: report.cover.urban, style: { backgroundColor: cover.urban } },
              { label: t("Eau"), pct: report.cover.water, style: { backgroundColor: cover.water } },
              { label: t("Ouvert"), pct: report.cover.open, style: { backgroundColor: cover.open } },
            ]}
          />
        ) : (
          <ReportRow label={t("Occupation du sol")}>{t("non évaluée (pas de données de couvert)")}</ReportRow>
        )}
      </ReportSection>

      {report.drones && <DronesReport drones={report.drones} phase={report.phase ?? "T1"} fly={fly} />}

      <ReportSection icon={<Users size={14} />} title={t("Dispositions et menace")} confidence="high">
        {report.dispositions.ami.length > 0 && <ReportRow label={t("Unités amies dans la zone")}>{report.dispositions.ami.length}</ReportRow>}
        {report.dispositions.ami.length > 0 && (
          <p className="text-meta leading-snug text-ink-soft">{report.dispositions.ami.join(", ")}</p>
        )}
        {report.dispositions.eni.length > 0 && <ReportRow label={t("Unités ennemies dans la zone")}>{report.dispositions.eni.length}</ReportRow>}
        {report.dispositions.eni.length > 0 && (
          <p className="text-meta leading-snug text-ink-soft">{report.dispositions.eni.join(", ")}</p>
        )}
        <ReportRow label={t("Zone sous portée ennemie")}>
          {fairThreat !== null
            ? t("{pct} ({fair} par beau temps)", { pct: percent(report.dispositions.threat_pct), fair: percent(fairThreat) })
            : percent(report.dispositions.threat_pct)}
        </ReportRow>
      </ReportSection>

      <div className="space-y-2 border-t border-line px-4 py-3">
        {report.warnings.length > 0 && (
          <Callout tone="warn">
            <ul className="space-y-1">
              {report.warnings.map((w) => (
                <li key={w.fr}>{text(w)}</li>
              ))}
            </ul>
          </Callout>
        )}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-meta text-ink-muted">
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-4 rounded-sm" style={hatch(false)} /> {t("SLOW : terrain restreint")}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-4 rounded-sm" style={hatch(true)} /> {t("NO-GO : très restreint")}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="size-2.5 rounded-full" style={{ background: OBSTACLE_GREEN }} /> {t("Points de passage obligés")}
          </span>
        </div>
      </div>
    </Motion>
  );
}

export function TerrainPanel() {
  const { terrain, canAnalyze, busyKind, error, refused, outdatedFor, analyzeCamp, analyzeZone, computeAxis, rerun, stale, clear } =
    useTerrainAnalysis();
  const draft = useUi((s) => s.draft);
  const setDraft = useUi((s) => s.setDraft);
  const terrainZone = useUi((s) => s.terrainZone);
  const setTerrainZone = useUi((s) => s.setTerrainZone);
  const overlay = useUi((s) => s.terrainOverlay);
  const setOverlay = useUi((s) => s.setTerrainOverlay);
  const select = useUi((s) => s.select);
  const drawing = draft?.purpose === "terrain";
  // Préalable manquant : le lien unique des Données initiales désigne le champ à remplir (R12).
  const openMissionData = useOpenMissionData();
  // Zone libre pas encore prête : ni couches ni relief à analyser ; les analyses désignent l'action attendue.
  const pendingZone = usePendingZone();
  const blockedAnalysis = !canAnalyze || pendingZone !== null;
  const onBlockedAnalysis = () => (pendingZone ? designateZoneWait(pendingZone, "terrain") : openMissionData());
  // Refus « terrain pas prêt » d'avant que la zone soit prête : sans objet, plus affiché.
  const outdated = outdatedFor(pendingZone);
  const shownError = outdated ? null : error;

  // Zone tracée sur la carte : analysée dès la fin du tracé.
  useEffect(() => {
    if (!terrainZone) return;
    analyzeZone(terrainZone.ring);
    setTerrainZone(null);
  }, [terrainZone?.at]);

  // Quitter la vue abandonne un tracé de zone en cours (le rapport affiché, lui, reste sur la carte).
  useEffect(
    () => () => {
      if (useUi.getState().draft?.purpose === "terrain") useUi.getState().setDraft(null);
    },
    [],
  );

  const startZone = () => {
    select(null);
    setOverlay(true);
    setDraft({
      purpose: "terrain",
      family: "operationalGraphics", // sans objet pour une zone d'analyse
      kind: "zone",
      name: t("Zone à analyser"),
      color: FRIEND_BLUE,
      points: [],
      hover: null,
    });
  };

  return (
    <FloatingPanel
      enter="left"
      width="view"
      icon={VIEW_META.terrain.icon}
      title={t("Analyse du terrain")}
      actions={
        <>
          {terrain && (
            <>
              <IconButton
                size="sm"
                onClick={() => setOverlay(!overlay)}
                title={overlay ? t("Masquer la praticabilité et l'observation sur la carte") : t("Afficher la praticabilité et l'observation sur la carte")}
                aria-label={overlay ? t("Masquer la praticabilité et l'observation sur la carte") : t("Afficher la praticabilité et l'observation sur la carte")}
                aria-pressed={!overlay}
              >
                {overlay ? <Eye size={16} /> : <EyeOff size={16} />}
              </IconButton>
              <IconButton size="sm" onClick={clear} title={t("Effacer l'analyse")} aria-label={t("Effacer l'analyse")}>
                <X size={16} />
              </IconButton>
            </>
          )}
          <HelpButton screen="terrain" buttonSize="sm" size={16} />
        </>
      }
    >
      {busyKind === "analysis" ? (
        <Motion origin="fade" className="flex flex-col items-center gap-3 px-6 py-10" role="status">
          {/* Anneau de progression de l'analyse (gardé : il dit l'avancement, pas seulement l'attente). */}
          <div className="relative size-16">
            <svg viewBox="0 0 64 64" className="size-16 -rotate-90">
              <circle cx="32" cy="32" r="27" fill="none" stroke="var(--color-line)" strokeWidth="5" />
              <circle cx="32" cy="32" r="27" fill="none" stroke="var(--color-accent)" strokeWidth="5" strokeLinecap="round"
                strokeDasharray="170" className="terrain-progress" />
            </svg>
            <MountainSnow size={24} className="absolute inset-0 m-auto text-ink-muted" />
          </div>
          <p className="text-title font-medium text-ink">{<Waiting>{t("Analyse du terrain…")}</Waiting>}</p>
          <p className="text-center text-ui text-ink-muted">{t("Relief, couvert, obstacles, praticabilité, terrain clé, menace et météo.")}</p>
        </Motion>
      ) : terrain ? (
        <>
          <div className="flex flex-wrap gap-2 px-4 pt-3">
            <Button
              variant="secondary"
              onClick={startZone}
              disabled={drawing}
              blocked={blockedAnalysis}
              onBlocked={onBlockedAnalysis}
              // Rapport d'avant la section « Drones » : l'action attendue par la ligne des éléments drones de l'analyse.
              data-attention={terrain.drones === undefined ? TERRAIN_ANALYZE_TARGET : undefined}
            >
              <ScanSearch size={14} /> {terrain.camp ? t("Analyser une zone") : t("Nouvelle zone")}
            </Button>
            {!terrain.camp && (
              <Button variant="ghost" onClick={analyzeCamp} blocked={blockedAnalysis} onBlocked={onBlockedAnalysis}>
                <Shield size={14} /> {t("Analyse de la zone d'opérations")}
              </Button>
            )}
          </div>
          {/* Météo, date ou fuseau du dossier changés depuis le calcul : le rapport le dit et se relance. */}
          <StaleCallout show={stale} busy={busyKind !== null} onRerun={rerun} blocked={blockedAnalysis} onBlocked={onBlockedAnalysis} />
          <Report report={terrain} onAxis={computeAxis} axisBusy={busyKind === "axis"} />
        </>
      ) : (
        <Motion origin="fade">
          <Presence show={pendingZone !== null}>
            <Motion origin="fade" className="px-4 pt-4">
              <ZoneWaitCallout />
            </Motion>
          </Presence>
          <div className="space-y-3 px-4 pt-4 pb-2">
            <p className="text-ui leading-relaxed text-ink-soft">
              {t(
                "Méthode OAKOC — observation, axes d'approche, terrain clé, obstacles, couvert — calculée sur les données du camp : relief, occupation du sol, routes, cours d'eau, ponts et points de passage obligés, face au dispositif affiché et par la météo du dossier.",
              )}
            </p>
            {/* Préalable manquant (aucun camp) : les cartes restent cliquables et désignent le champ à remplir. */}
            <ChoiceCard
              icon={Shield}
              title={t("Analyse de la zone d'opérations")}
              detail={t("Toute l'emprise de l'exercice : praticabilité, axes, coupures, points hauts, verdict pour votre mission.")}
              data-attention={TERRAIN_ANALYZE_TARGET}
              onClick={analyzeCamp}
              blocked={blockedAnalysis}
              onBlocked={onBlockedAnalysis}
            />
            <ChoiceCard
              icon={ScanSearch}
              title={t("Analyser une zone")}
              detail={t("Tracez-la sur la carte, sommet par sommet ; l'axe favorable se calcule ensuite à travers elle.")}
              onClick={startZone}
              disabled={drawing}
              blocked={blockedAnalysis}
              onBlocked={onBlockedAnalysis}
            />
          </div>
          {!canAnalyze && (
            <EmptyState variant="inline" action={<OpenMissionDataButton />}>
              {t("Choisissez d'abord un camp et une mission dans les Données initiales.")}
            </EmptyState>
          )}
        </Motion>
      )}
      <Presence show={Boolean(shownError)}>
        {shownError && (
          <div className="px-4 pb-3">
            {refused ? <ZoneWaitCallout message={shownError} /> : <Callout tone="danger">{shownError}</Callout>}
          </div>
        )}
      </Presence>
    </FloatingPanel>
  );
}
